<script setup>
import LinkTo from "@/components/atoms/link-to";
import LazyImage from "@/components/atoms/lazy-image";
import Title from "@/components/atoms/titre";
import PopinAnchor from "@/components/molecules/popin-anchor";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Form from "@/components/molecules/form";
import Button from "@/components/atoms/button";
import carteFidPassOmbre from "@/assets/images/carrefour-club-pass-card-ombre-incline.svg";
import cartePass from "@/assets/images/carte-pass-carrefour.svg";
import carteFid from "@/assets/images/carte-club-carrefour.svg";
import Captcha from "@/components/molecules/captcha";
import Popin from "@/containers/popin-adaptive";
import PopinOuTrouverCarrefour from "@/components/organisms/mon-compte/popin-outrouver-carrefour";
import ButtonIcon from "@/components/molecules/button-icon";
import { useFideliteCarrefour } from "@/composables/fidelite_carrefour";

import { required, minLength, maxLength, numeric } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

const props = defineProps({
  isValidCard: {
    type: Boolean,
    required: false,
    default: false
  },
  messageError: {
    type: String,
    required: false,
    default: null
  },
  isLoading: {
    type: Boolean,
    default: false,
    required: true
  },
  errorPin: {
    type: Boolean,
    required: false,
    default: false
  },
  carteCarrefour: {
    type: Object,
    default: null
  },
  // distinction par rapport à l'appairage
  isAjoutCompte: {
    type: Boolean,
    required: false,
    default: false
  },
  ...formValidationProps()
});

const haveCarteCarrefour = computed(() => {
  return !!props.carteCarrefour;
});

const emit = defineEmits(["prevalidate", "validate", "ou-trouver"]);

// Form data
const numero = ref(
  haveCarteCarrefour.value
    ? props.carteCarrefour.num_carte_carrefour
    : undefined
);
const pin = ref(undefined);
const captcha = ref(null);
const captchaToken = ref(null);
const outrouverPopin = ref(null);
const { isTemps2Fidelite } = useFideliteCarrefour();

const { getState, getErrorMessage, checkValidation } = useFormValidation(props);

// Validations schema
const validationsPrecheck = computed(() => {
  return {
    numero: {
      required,
      minLength: minLength(16),
      maxLength: maxLength(19),
      numeric
    }
  };
});

const validations = computed(() => {
  return {
    pin: {
      required,
      minLength: minLength(4),
      maxLength: maxLength(4),
      numeric
    }
  };
});

// Validators
const vp$ = useVuelidate(
  validationsPrecheck,
  { numero },
  { $stopPropagation: true }
);

const v$ = useVuelidate(validations, { pin }, { $stopPropagation: true });

const prevalidate = () => {
  return checkValidation(vp$.value);
};

const validateComplete = () => {
  if (haveCarteCarrefour.value) {
    return checkValidation(v$.value);
  }
  return checkValidation(vp$.value) && checkValidation(v$.value);
};

// Captcha validator
const verifyCaptcha = async (token) => {
  if (token) {
    captchaToken.value = token;
  }

  const model = {
    num_carte_carrefour: haveCarteCarrefour.value
      ? props.carteCarrefour?.id
      : numero.value,
    code_pin_carrefour: pin.value ?? undefined,
    "g-captcha-response": captchaToken.value
  };

  if ((numero.value && pin.value) || (haveCarteCarrefour.value && pin.value)) {
    emit("validate", model);
  } else {
    emit("prevalidate", model);
  }
};

const handleClick = () => {
  // Reset du captcha avant de relancer la validation
  captcha.value.reset();

  // On est dans l'étape 1 de prévalidation
  if (!props.isValidCard && prevalidate()) {
    captcha.value.execute();
  } else if (props.isValidCard && validateComplete()) {
    captcha.value.execute();
  }
};

const isCarteFid = computed(
  () =>
    numero?.value?.startsWith("9") ||
    numero?.value === undefined ||
    numero?.value === ""
);
</script>

<template>
  <Form
    class="u-text-center u-pb-md"
    autocomplete="off"
    @submit.prevent="handleClick"
  >
    <LazyImage
      class="u-mx-auto"
      :src="carteFidPassOmbre"
      width="80"
      height="80"
    />
    <Title
      tag="h2"
      font="secondary"
      variant="primary"
      class="u-mb-xs"
    >
      <template v-if="!isAjoutCompte && haveCarteCarrefour">
        Saisissez le Code Cagnotte de votre Carte Club ou Carte PASS
      </template>
      <template v-else-if="!isAjoutCompte">
        Enregistrer ma Carte Club ou ma Carte PASS
      </template>
      <template v-else>
        Ajouter votre Carte Club ou votre Carte PASS
      </template>
    </Title>

    <p :class="isTemps2Fidelite ? 'u-mb-md' : 'u-max-width-37 u-mx-auto u-mb-md'">
      <template v-if="!isAjoutCompte && haveCarteCarrefour">
        Pour valider le transfert de votre cagnotte Cora.
      </template>
      <template v-else-if="!isAjoutCompte">
        Pour ne perdre aucune de vos cagnottes Cora, enregistrez dès maintenant votre Carte Club ou votre Carte PASS. <br>
        Votre Carte Club et votre Carte PASS ne sont pas utilisables sur cora.fr.
      </template>
      <template v-else>
        C’est la première étape pour profiter de votre Fidélité Carrefour.
      </template>
    </p>

    <div class="u-mx-auto u-max-width-32">
      <div class="u-mb-sm u-text-left ">
        <FormGroup
          v-if="!haveCarteCarrefour"
          size="lg"
          :state="getState(vp$, 'num_carte_carrefour', 'numero')"
        >
          <FormInput
            v-model="numero"
            size="lg"
            type="text"
            inputmode="decimal"
            pattern="^[0-9]*"
            name="numCarteCarrefour"
            id="numCarteCarrefour"
            autocomplete="off"
            :imgPath="isCarteFid ? carteFid : cartePass"
            :state="getState(vp$, 'num_carte_carrefour', 'numero')"
            @blur="vp$.numero.$touch()"
            @input="vp$.numero.$reset()"
          >
            N° de fidélité
          </FormInput>
          <template #message>
            <p v-if="vp$.numero.required.$invalid">
              Merci de renseigner un numéro de carte fidélité valide.
            </p>
            <p v-else-if="vp$.numero.maxLength.$invalid || vp$.numero.minLength.$invalid">
              Le numéro de carte doit être compris entre 16 et 19 chiffres.
            </p>
            <p v-else-if="vp$.numero.numeric.$invalid">
              Merci de renseigner un numéro de carte constitué uniquement de chiffres
            </p>
            <p v-else>
              {{ getErrorMessage("num_carte_carrefour", "Le numéro de carte est invalide. Merci de vérifier votre saisie") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          v-if="isValidCard || haveCarteCarrefour"
          :state="getState(v$, 'code_pin_carrefour', 'pin')"
          size="lg"
          class="u-mt-md"
        >
          <FormInput
            size="lg"
            name="pin"
            id="pin"
            inputmode="decimal"
            pattern="^[0-9]*"
            v-model="pin"
            :state="getState(v$, 'code_pin_carrefour', 'pin')"
            type="password"
            @blur="v$.pin.$touch()"
            @input="v$.pin.$reset()"
          >
            Code Cagnotte
          </FormInput>
          <template #message>
            <template v-if="v$.pin.required.$invalid">
              Merci de renseigner un Code Cagnotte valide.
            </template>
            <template v-else-if="v$.pin.maxLength.$invalid || v$.pin.minLength.$invalid || v$.pin.numeric.$invalid">
              <p>Le Code Cagnotte doit être composé de 4 chiffres.</p>
            </template>
            <template v-else>
              {{ getErrorMessage("code_pin_carrefour", "Le Code Cagnotte est invalide. Merci de vérifier votre saisie") }}
            </template>
          </template>
        </FormGroup>

        <p
          v-if="messageError"
          class="u-mt-xs u-text-center u-font-14 u-color-alert"
        >{{ messageError }}</p>

        <Button
          @click="outrouverPopin.show()"
          link
          class="u-mt-xs"
          v-if="!isValidCard"
        >
          Où trouver mon numéro de fidélité ?
        </Button>

        <div
          v-else
          class="u-flex u-flex-justify-between u-flex-wrap u-gap-xs"
        >
          <PopinAnchor
            infobulle
            position="top-right"
            popin-size="sm"
            link
            open-on-hover
            class="u-mt-xs"
          >
            <template #button-content>
              Quel code saisir ?
            </template>
            <p class="u-font-14">Ici, vous devez saisir votre Code Cagnotte à 4 chiffres, et non le code bancaire de votre Carte PASS.</p>
          </PopinAnchor>
          <ButtonIcon
            link
            icon-color="grey-10"
            icon-size="sm"
            icon-name="external"
            icon-position="right"
            class="u-mt-xs"
            target="_blank"
            to="https://www.carrefour.fr/mon-compte/fidelite/changer-mon-code-secret"
          >
            Réinitialiser mon code
          </ButtonIcon>
        </div>
      </div>
    </div>
    <p class="u-font-10 u-mt-xs u-mb-sm u-text-center">
      En renseignant mon numéro de fidélité Carrefour et en cliquant sur “Je renseigne”, j'accepte la communication par Cora à Carrefour France de mes données personnelles. La transmission de ces données personnelles est nécessaire à Carrefour France pour pouvoir me reconnaître et me faire bénéficier des avantages de ma carte de Fidélité Cora chez Carrefour. Cora et Carrefour agissant en responsable de traitement indépendant pour les deux programmes.
    </p>

    <Button
      :disabled="isLoading"
      size="md"
      variant="primary"
      radius="full"
      value="Je renseigne"
      type="submit"
    />
    <Captcha
      ref="captcha"
      @verify="verifyCaptcha"
    />

    <p
      class="u-font-10 u-text-left u-mt-md"
      style="line-height: 1.2rem;"
    >Les données personnelles recueillies dans ce formulaire font l’objet d’un traitement par la société Cora, en tant que responsable de traitement, pour les finalités suivantes :

      Gestion du programme de fidélité (les avantages peuvent être ciblés et personnalisés en fonction de votre profil) / Gestion des services réservés aux membres du Programme de fidélité / Traitement des questions ou réclamations / Envoi d’informations non commerciales / Gestion du recouvrement / Élaboration de statistiques, études ou analyses commerciales ou techniques, recueils d’avis / Dématérialisation de l’inscription au programme de fidélité / Lutte contre la fraude / Gestion d’un éventuel rappel de produit auprès du consommateur / Accès aux justificatifs d’achats (factures, tickets de caisses et autres) / Gérer et préparer une action en justice ou devant une administration, un médiateur ou un arbitre et, le cas échéant, exécuter la décision rendue / Exercice de vos droits sur vos données personnelles / Gestion de la comptabilité / Gestion de vos consentements et autorisations concernant l’utilisation de vos données personnelles / Envoi d’informations et d’offres commerciales par Cora.

      Conformément au Règlement (UE) 2016/679 relatif à la protection des données à caractère personnel et à la Loi n°78-17 du 06 janvier 1978 modifiée, vous disposez, en fonction du fondement juridique du traitement, des droits suivants sur vos données : droit d’accès, droit de rectification, droit à l’effacement (droit à l’oubli), droit d’opposition, droit à la limitation du traitement et droit à la portabilité.

      Vous pouvez également définir les directives relatives à la conservation, à l’effacement et à la communication de vos données à caractère personnel après votre décès.

      Pour exercer vos droits rendez-vous sur le site

      <LinkTo
        target="_blank"
        to="https://www.cora.fr/donnees-personnelles"
      >https://www.cora.fr/donnees-personnelles
      </LinkTo>

      Pour plus de renseignements, veuillez consulter la
      <LinkTo
        target="_blank"
        to="/legal/informations-juridiques/politique-donnees-personnelles-fidelite"
      >Politique Données Personnelles- Programme de fidélité de Cora.
      </LinkTo>
    </p>
  </Form>
  <Popin
    ref="outrouverPopin"
    size="lg"
  >
    <PopinOuTrouverCarrefour />
  </Popin>
</template>
